// The Death Register (epic-3 #15) — not a separate entity, just the filtered
// view over DischargeProcess rows with outcome='deceased' (ADR 0005), joined
// client-side to the admission rows the shell already holds.
//
// Deceased rows are joined across ALL admission statuses, not just Discharged:
// a death is declared at the Ward stage, and the admission stays Allocated /
// Admitted until Physical Discharge closes it — so a patient can be in the
// register while their body is still on the ward.

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../../../nurse-dashboard/theme";
import MccdForm, { mccdFormFromProcess, mccdFormToPayload } from "../../components/MccdForm";
import {
  MANNER_OF_DEATH_LABELS,
  updateMccd,
} from "../../api/dischargeProcessApi";
import { downloadMccdForm4, downloadRelativeSlip } from "../../utils/mccdPdf";
import RegisterToolbar from "./RegisterToolbar";
import { fmt } from "./registerUtils";

// Identity/hospital fields the DischargeProcess row doesn't carry, assembled for
// the PDF documents. Hospital name is a small follow-up (facility branding).
function pdfContext(row) {
  return {
    patientName: row.patientName,
    uhid: row.uhid,
    sex: row.gender,
    age: row.age,
    ward: row.ward,
    admittedAt: row.admittedAt,
  };
}

const COLUMNS = [
  { header: "Admission ID", get: (r) => r.admissionId ?? "-" },
  { header: "Patient Name", get: (r) => r.patientName },
  { header: "UHID", get: (r) => r.uhid || "-" },
  { header: "Age", get: (r) => (r.age != null ? r.age : "-") },
  { header: "Gender", get: (r) => r.gender || "-" },
  { header: "Ward", get: (r) => r.ward },
  { header: "Admitting Doctor", get: (r) => r.admittingDoctor },
  { header: "Admitted At", get: (r) => fmt(r.admittedAt) },
  { header: "Time of Death", get: (r) => fmt(r.timeOfDeath) },
  { header: "Cause of Death", get: (r) => r.causeOfDeath || "-" },
  {
    header: "Manner of Death",
    get: (r) => MANNER_OF_DEATH_LABELS[r.mannerOfDeath] || "-",
  },
  { header: "Certifying Doctor", get: (r) => r.certifyingDoctorName || "-" },
  { header: "Death Certificate No.", get: (r) => r.deathCertificateNumber || "-" },
  { header: "Admission Status", get: (r) => r.status },
];

// A row is only "complete" once the register can actually be filed on it. These
// are the same four fields the backend gates body release on — time of death is
// included, because a death record without one certifies nothing (and one of this
// tenant's two deceased patients was discharged without it).
//
// The certificate number is deliberately excluded: it legitimately arrives days
// later, so its absence is a normal state, not an incomplete record.
function isComplete(row) {
  return Boolean(
    row.timeOfDeath &&
      row.causeOfDeath &&
      row.mannerOfDeath &&
      row.certifyingDoctorId
  );
}

export default function DeathRegisterView({ rows, loading, onRefresh }) {
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const openEdit = (row) => {
    setEditRow(row);
    // The MCCD editor reads the structured cause off the raw DischargeProcess row.
    setForm(mccdFormFromProcess(row.raw || {}));
  };

  const closeEdit = () => {
    setEditRow(null);
    setForm(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await updateMccd(editRow.dischargeProcessId, mccdFormToPayload(form));
      if (data?.status === "Success") {
        toast.success(data.message || "MCCD saved.");
        (data.warnings || []).forEach((w) => toast.warn(w));
        closeEdit();
        onRefresh();
      } else {
        toast.error(data?.message || "Could not save the MCCD.");
      }
    } catch (err) {
      console.error("Save MCCD failed:", err?.response?.data || err?.message);
      toast.error(err?.response?.data?.message || "Could not save the MCCD.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadMccd = (row) => {
    try {
      downloadMccdForm4(row.raw || {}, pdfContext(row));
    } catch (err) {
      toast.error(err?.message || "Could not generate the MCCD.");
    }
  };
  const handleDownloadSlip = (row) => {
    try {
      downloadRelativeSlip(row.raw || {}, pdfContext(row));
    } catch (err) {
      toast.error(err?.message || "Could not generate the slip.");
    }
  };

  return (
    <Box>
      <RegisterToolbar
        title="Death Register"
        columns={COLUMNS}
        rows={rows}
        loading={loading}
        onRefresh={onRefresh}
      />

      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
                <TableCell>Admission</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Ward</TableCell>
                <TableCell>Time of Death</TableCell>
                <TableCell>Cause of Death</TableCell>
                <TableCell>Manner</TableCell>
                <TableCell>Certifying Doctor</TableCell>
                <TableCell>Certificate No.</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: NURSE_COLORS.textSecondary }}>
                    {loading ? "Loading…" : "No deceased patients recorded"}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.dischargeProcessId}>
                  <TableCell>{r.admissionId}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {r.patientName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[r.uhid, r.age != null ? `${r.age}${r.gender}` : null]
                        .filter(Boolean)
                        .join(" · ") || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.ward}</TableCell>
                  <TableCell>{fmt(r.timeOfDeath)}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{r.causeOfDeath || "—"}</TableCell>
                  <TableCell>
                    {r.mannerOfDeath ? (
                      <Chip
                        size="small"
                        label={MANNER_OF_DEATH_LABELS[r.mannerOfDeath] || r.mannerOfDeath}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>{r.certifyingDoctorName || "—"}</TableCell>
                  <TableCell>{r.deathCertificateNumber || "—"}</TableCell>
                  <TableCell align="right">
                    {!isComplete(r) && (
                      <Chip
                        size="small"
                        label="Incomplete"
                        color="warning"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                    <Tooltip title="Certify / edit cause of death (MCCD)">
                      <IconButton size="small" onClick={() => openEdit(r)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* Documents are only meaningful once the death is certified. */}
                    <Tooltip title={isComplete(r) ? "Download MCCD (Form 4)" : "Certify the death first"}>
                      <span>
                        <IconButton size="small" disabled={!isComplete(r)} onClick={() => handleDownloadMccd(r)}>
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Download certificate-to-relative slip (no cause of death)">
                      <span>
                        <Button size="small" onClick={() => handleDownloadSlip(r)}>
                          Slip
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Dialog open={Boolean(editRow)} onClose={saving ? undefined : closeEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Certify Cause of Death (MCCD Form 4)
          {editRow && (
            <Typography variant="body2" color="text.secondary">
              {editRow.patientName} · Admission {editRow.admissionId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {form && (
            <MccdForm
              value={form}
              onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
              disabled={saving}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} variant="outlined" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
